import React, { useEffect, useRef } from 'react';

const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

interface Props {
    onVerify: (token: string) => void;
    onExpire?: () => void;
    /**
     * Değeri her değiştiğinde pencere sıfırlanır ve yeni bir jeton istenir.
     *
     * NEDEN GEREKLİ: Turnstile jetonu TEK KULLANIMLIKTIR. Şifre yanlış
     * girildiğinde jeton sunucuda harcanmış oluyor; ekran aynı jetonu ikinci
     * kez gönderince Cloudflare doğrulamayı reddediyor ve kullanıcı "robot
     * doğrulaması başarısız" hatasına takılıp sayfayı yenilemeden giriş
     * yapamıyor. Giriş başarısız olduğunda bu sayacı artırın.
     */
    resetKey?: number;
}

declare global {
    interface Window {
        turnstile?: {
            render: (el: HTMLElement, opts: object) => string;
            reset: (widgetId: string) => void;
            remove: (widgetId: string) => void;
        };
        onTurnstileLoad?: () => void;
    }
}

// Turnstile enabled only when site key is configured
export const turnstileEnabled = Boolean(SITE_KEY);

export const TurnstileWidget: React.FC<Props> = ({ onVerify, onExpire, resetKey }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    const ilkRef = useRef(true);

    useEffect(() => {
        if (!SITE_KEY || !containerRef.current) return;

        const renderWidget = () => {
            if (!containerRef.current || widgetIdRef.current) return;
            widgetIdRef.current = window.turnstile!.render(containerRef.current, {
                sitekey: SITE_KEY,
                callback: (token: string) => onVerify(token),
                'expired-callback': () => {
                    onExpire?.();
                    if (widgetIdRef.current) window.turnstile?.reset(widgetIdRef.current);
                },
                theme: 'light',
            });
        };

        if (window.turnstile) {
            renderWidget();
        } else {
            window.onTurnstileLoad = renderWidget;
            if (!document.getElementById('cf-turnstile-script')) {
                const script = document.createElement('script');
                script.id = 'cf-turnstile-script';
                script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);
            }
        }

        return () => {
            if (widgetIdRef.current && window.turnstile) {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
            }
        };
    }, []);

    // İlk çizimde sıfırlama yapılmaz: pencere zaten yeni.
    useEffect(() => {
        if (ilkRef.current) { ilkRef.current = false; return; }
        if (widgetIdRef.current && window.turnstile) {
            window.turnstile.reset(widgetIdRef.current);
            onExpire?.();
        }
    }, [resetKey]);

    if (!SITE_KEY) return null;

    return <div ref={containerRef} className="flex justify-center mt-2" />;
};
